import type { BudgetType, BudgetTreatment } from '@/lib/constants';
import { treatmentsForCategoryType } from '@/lib/utils/budget';
import { formatCurrency } from '@/lib/utils/currency';
import { formatPercent, formatPercentGlance } from '@/lib/utils/percent';

export const translateTransactionType = (type: 'income' | 'expense'): string => {
  const translations: Record<string, string> = {
    income: 'Receita',
    expense: 'Despesa',
  };
  return translations[type] || type;
};

// "goals" reads "Objetivos", not "Metas" — that would collide with the Metas page (/goals) and the
// goal set itself ("Metas › Metas 20%"). Open per docs/specs/budgets/budgets-trd.md §12.3.
const BUDGET_TYPE_LABELS: Record<BudgetType, string> = {
  'fixed-expenses': 'Custos fixos',
  comfort: 'Conforto',
  goals: 'Objetivos',
  pleasure: 'Prazeres',
  'financial-freedom': 'Liberdade financeira',
  knowledge: 'Conhecimento',
};

export const translateBudgetType = (type: BudgetType): string => BUDGET_TYPE_LABELS[type];

// What each group is for, in one line — said under its name on Metas, where the split is decided.
const BUDGET_TYPE_HELP: Record<BudgetType, string> = {
  'fixed-expenses': 'O que vem todo mês: moradia, contas, mercado, transporte.',
  comfort: 'O que deixa a rotina mais fácil, sem ser essencial.',
  goals: 'Dinheiro guardado para algo com data: uma viagem, um carro, uma compra planejada.',
  pleasure: 'Lazer e diversão: restaurantes, passeios, hobbies.',
  'financial-freedom': 'Investimentos de longo prazo, para a renda que trabalha por você.',
  knowledge: 'Cursos, livros e tudo o que investe em você.',
};

export const budgetTypeHelp = (type: BudgetType): string => BUDGET_TYPE_HELP[type];

const BUDGET_TREATMENT_LABELS: Record<BudgetTreatment, string> = {
  income: 'Renda',
  spend: 'Gasto',
  refund: 'Reembolso',
  earmark: 'Reforço',
  deduction: 'Dedução',
  excluded: 'Fora do orçamento',
};

export const translateBudgetTreatment = (treatment: BudgetTreatment): string => BUDGET_TREATMENT_LABELS[treatment];

const BUDGET_TREATMENT_HELP: Record<BudgetTreatment, string> = {
  income: 'Entra no total do mês que divide suas metas',
  spend: 'Consome o limite do grupo escolhido',
  refund: 'Devolve dinheiro ao grupo; não conta como renda',
  earmark: 'Aumenta o limite apenas daquele grupo',
  deduction: 'Reduz o limite daquele grupo; não é um gasto',
  excluded: 'Transferências entre suas próprias contas, por exemplo',
};

export const budgetTreatmentHelp = (treatment: BudgetTreatment): string => BUDGET_TREATMENT_HELP[treatment];

export const BUDGET_UNASSIGNED_LABEL = 'Sem classificação';
export const BUDGET_UNASSIGNED_HELP = 'Não entra em nenhum grupo — aparece como não contabilizado';
export const BUDGET_UNSUPPORTED_LABEL = 'Classificação não reconhecida';

// Column words for the Orçamento Doméstico table (§8, §12.3).
export const BUDGET_COLUMN_LABELS = {
  mustSpend: 'Limite',
  spent: 'Gasto',
  used: 'Utilizado',
  share: 'da renda',
} as const;

// One line for a category's assignment — "Gasto · Custos fixos", or just the treatment when it takes no
// bucket. Also what the save announcement reads out.
export const budgetAssignmentSummary = (treatment: BudgetTreatment, bucket?: BudgetType | ''): string =>
  bucket ? `${translateBudgetTreatment(treatment)} · ${translateBudgetType(bucket)}` : translateBudgetTreatment(treatment);

// Category assignment rows on Categorias (§11).
export const BUDGET_CHOOSE_BUCKET_HINT = 'escolha o grupo';
export const BUDGET_BUCKET_PLACEHOLDER = 'Escolha o grupo';
export const BUDGET_LEGEND_TITLE = 'Como classificar';
// A Categorias row reads as its summary and opens in place to its choices; the rare treatments wait one more step.
export const BUDGET_ROW_TOGGLE = { classify: 'Classificar', edit: 'Alterar', close: 'Fechar' } as const;
export const BUDGET_MORE_TREATMENTS = { show: 'Mais opções', hide: 'Menos opções' } as const;

export const budgetTreatmentGroupLabel = (categoryName: string): string => `Classificação de ${categoryName}`;
export const budgetBucketLabel = (categoryName: string): string => `Grupo de ${categoryName}`;
export const budgetSavedAnnouncement = (categoryName: string, summary: string): string => `${categoryName}: ${summary}`;

export const BUDGET_ALL_CLASSIFIED = {
  title: 'Tudo classificado',
  description: 'Nenhuma categoria está sem classificação.',
};

// Toast copy for a failed category update (§12.1–12.2), shaped like ErrorInfo.
export const CATEGORY_NOT_FOUND = {
  title: 'Categoria não encontrada',
  description: 'Ela pode ter sido removida. A lista foi atualizada.',
};

const joinLabels = (labels: string[]): string =>
  labels.length < 2 ? labels.join('') : `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;

const legalTreatments = (categoryType: 'income' | 'expense'): string =>
  joinLabels(treatmentsForCategoryType(categoryType).map(translateBudgetTreatment));

// The pills only offer legal pairs, so a 422 here is a drift signal, not something the user did: the copy
// restates the matrix instead of blaming them, from the same rule the pills are built on.
export const BUDGET_ASSIGNMENT_INVALID = {
  title: 'Classificação inválida',
  description: `Esta classificação não combina com o tipo da categoria. Receitas aceitam ${legalTreatments('income')}; despesas aceitam ${legalTreatments('expense')}.`,
};

// Painel's two sections, stacked under one period bar. The cash flow comes first because it needs nothing from the
// user; the budget needs their categories classified. Análise is how the cash flow was named before the two pages
// were joined — it no longer appears on screen, only as the `Analysis*` names in code and the /analysis redirect.
export const DASHBOARD_CASH_FLOW_HEADING = 'Fluxo de caixa';
export const DASHBOARD_BUDGET_HEADING = 'Orçamento';
export const DASHBOARD_LOADING_LABEL = 'Carregando';
export const DASHBOARD_SKIP_TO_BUDGET = 'Ir para o orçamento';

// The aside beside a section head says which period the section reads, so it still does once the period bar has
// scrolled away (on a phone Orçamento starts well below it). The budget is monthly, so it names the cadence too.
export const budgetSectionAside = (periodLabel?: string): string =>
  periodLabel ? `${periodLabel} · mensal` : 'mensal';

// The verdict line under the period bar: the month's answer in one sentence, before any chart, with a way to the
// detail. "acima do limite" counts the rows that carry the expense dot (over, or spending with no limit).
// For the current month it also says how far in it is ("dia 18 de 30"): 52% used means one thing on the 5th and
// another on the 28th. A date, not money — nothing is calculated from the amounts.
export const budgetVerdictUsed = (month: string, overallUsed: string, day?: { day: number; of: number }): string =>
  `${month}${day ? `, dia ${day.day} de ${day.of}` : ''}: ${formatPercentGlance(overallUsed)} do limite usado`;
export const budgetVerdictNoLimit = (month: string): string => `${month}: sem limite neste mês`;
export const budgetPastLimit = (count: number): string =>
  count === 0
    ? 'todos os grupos dentro do limite'
    : `${count} ${count === 1 ? 'grupo acima do limite' : 'grupos acima do limite'}`;
export const BUDGET_VERDICT_FIRST_RUN = 'Seu orçamento ainda não começou.';
export const BUDGET_VERDICT_CLASSIFY = 'Classificar categorias';

// From an Orçamento row to what fed it: Categorias narrowed to that group, and from each category its
// transactions for the month. The row's name is the link; a row past its limit also says it in words.
export const BUDGET_SEE_CATEGORIES = 'Ver categorias';
export const budgetGroupFilterLabel = (groupName: string): string => `Grupo: ${groupName}`;
export const BUDGET_GROUP_FILTER_CLEAR = 'Ver todas';
export const BUDGET_GROUP_EMPTY = {
  title: 'Nenhuma categoria neste grupo',
  description: 'Classifique uma categoria como Gasto, Reembolso, Reforço ou Dedução deste grupo para vê-la aqui.',
};
export const BUDGET_CATEGORY_TRANSACTIONS = 'Transações';
export const budgetCategoryTransactionsLabel = (categoryName: string): string => `Ver transações de ${categoryName}`;

// The pending queue's progress, and its end: rows stay listed after they are classified (for undo), so the
// count is what says the work is done.
export const budgetPendingProgress = (done: number, total: number): string =>
  `${done} de ${total} ${total === 1 ? 'classificada' : 'classificadas'}`;
export const BUDGET_PENDING_DONE = 'Tudo classificado.';
export const BUDGET_PENDING_DONE_ACTION = 'Ver todas as categorias';

// How to read Orçamento, one line per word a row can carry — opened from the foot of the section.
export const BUDGET_READING_HELP_TITLE = 'Como ler o orçamento';
export const BUDGET_READING_HELP: ReadonlyArray<{ term: string; meaning: string }> = [
  { term: 'meta', meaning: 'a parte da sua renda que você reservou para o grupo, em Metas.' },
  { term: 'acima', meaning: 'o grupo passou da meta deste mês — vale para gastos e para o que você guarda.' },
  { term: 'sem limite', meaning: 'houve movimento num grupo com meta de 0% ou sem renda no mês.' },
  { term: 'reembolso', meaning: 'dinheiro que voltou para um grupo; reduz o Gasto, não é renda.' },
  { term: 'dedução', meaning: 'um valor que diminui o limite do grupo neste mês.' },
];

// In Orçamento's place while a custom range is applied: its limits are a share of one month's income, so it has
// nothing to say about a range. The button beside it returns to the month the range starts in.
export const DASHBOARD_BUDGET_MONTH_ONLY = 'O orçamento é mensal. Volte para um mês para ver seus limites.';
export const DASHBOARD_BACK_TO_MONTH = 'Voltar para o mês';

// Cash-flow section: the period's movements — every one in and out, custom ranges included, whatever its category
// is classified as. "Entradas"/"Saídas" are the API's `income`/`expense` (`/reports`), which count it all.
// Orçamento's "Renda"/"Gasto" (`income`/`total_spent`, `/budgets`) count only what the user classified as income
// or spend, so the two pairs differ by what classification leaves out or nets off (transfers between the user's
// own accounts, refunds, earmarks, deductions, unclassified categories). Different words for different measures:
// never label one pair with the other's name.
export const ANALYSIS_SUMMARY_LABEL = 'Resumo do período';
export const ANALYSIS_INCOME_LABEL = 'Entradas no período';
export const ANALYSIS_EXPENSE_LABEL = 'Saídas no período';
export const ANALYSIS_BALANCE_LABEL = 'Saldo no período';
export const ANALYSIS_INCOME_HEADING = 'Entradas por categoria';
export const ANALYSIS_EXPENSE_HEADING = 'Saídas por categoria';
export const ANALYSIS_INCOME_NONE = 'Nenhuma entrada registrada neste período.';
export const ANALYSIS_EXPENSE_NONE = 'Nenhuma saída registrada neste período.';
export const ANALYSIS_EMPTY = {
  title: 'Sem dados neste período',
  description: 'Adicione transações para ver seu fluxo de caixa.',
  action: 'Adicionar transação',
};
export const ANALYSIS_LOAD_ERROR_TITLE = 'Não foi possível carregar o fluxo de caixa';

// Orçamento reading (§8). The figure labels reuse BUDGET_COLUMN_LABELS ("Gasto", "Utilizado").
export const BUDGET_INCOME_LABEL = 'Renda do mês';
export const BUDGET_SUMMARY_LABEL = 'Resumo do mês';
export const BUDGET_LINES_HEADING = 'Grupos do orçamento';

// Said once, above the figures, because the cash flow over it counts every movement and these count less. Exact
// where it matters: a transfer is out whether the user marked it "Fora do orçamento" or left its category alone
// (that money shows up in the unclassified strip instead). The words are the two figure labels' own.
export const BUDGET_SCOPE_NOTE =
  'Aqui conta só o que você classificou em Categorias; transferências entre suas contas ficam de fora. Por isso Renda e Gasto podem diferir de Entradas e Saídas.';

// The goal (percentage) and the actual share of income are different numbers that both read "% da renda":
// the goal says "meta", the share says "da renda".
export const budgetGoalMeta = (percentage: string): string => `meta ${formatPercent(percentage)}`;
export const budgetLimitOf = (mustSpend: number): string => `de ${formatCurrency(mustSpend)}`;

// Short notes on a bucket row whose state is not a plain percentage (§9). Over the limit the row shows "acima"
// beside the number and the dot, and a screen reader hears the whole "acima do limite".
export const BUDGET_LINE_NOTES = {
  refunded: 'reembolso',
  // A refund row's amount: what came back, said as a positive amount rather than "−R$ 50 de R$ 1.005".
  refundedAmount: 'em reembolsos',
  deduction: 'dedução',
  unbounded: 'sem limite',
  over: 'acima do limite',
  overShort: 'acima',
  noPercentage: 'sem percentual',
} as const;

// A total limit of zero (or less) has no percentage to speak of — the same rule as a bucket row (§9).
export const BUDGET_NO_LIMIT = 'sem limite neste mês';

export const BUDGET_NO_INCOME_HINT =
  'Nenhuma renda registrada neste mês. Classifique a categoria do seu salário como Renda para ver seus limites.';

// Every category unclassified: six zero rows would be noise, so the page teaches the next step instead (§8.6).
export const BUDGET_FIRST_RUN = {
  title: 'Seu orçamento começa nas categorias',
  description:
    'Classifique suas categorias — o que é renda, o que é gasto e a que grupo cada uma pertence. Depois disso, seus limites do mês aparecem aqui.',
  action: 'Ir para Categorias',
};

// The API's `unassigned` sums money moving in both directions (§3), so this says "movimentações", never
// "gastos". The category count is a separate fact: it says nothing about which categories the money is in.
export const budgetUnassignedAmount = (amount: string): string =>
  `Movimentações de ${formatCurrency(amount)} neste mês estão em categorias sem classificação e não foram contabilizadas.`;
export const budgetUnassignedCount = (count: number): string =>
  `${count} ${count === 1 ? 'categoria sem classificação' : 'categorias sem classificação'} no total.`;
export const BUDGET_CLASSIFY_ACTION = 'Classificar';
export const BUDGET_VIEW_CATEGORIES = 'Ver categorias';

export const BUDGET_GOALS_LABEL = 'Metas';
export const BUDGET_ADJUST_GOALS = 'Ajustar metas';

export const BUDGET_LOAD_ERROR_TITLE = 'Não foi possível carregar o orçamento';
export const BUDGET_RETRY = 'Tentar novamente';

// Metas page (§10). The page title is BUDGET_GOALS_LABEL and the bucket names come from translateBudgetType.
export const BUDGET_GOALS_PAGE_EYEBROW = 'Como sua renda se divide';
export const BUDGET_GOALS_TOTAL_LABEL = 'Total';
export const BUDGET_GOALS_LIST_HEADING = 'Meta de cada grupo';
export const BUDGET_VIEW_BUDGET = 'Ver orçamento';

// The running total's verdict, worded per rule (§10.3): what is missing or over, never a bare "inválido".
// "Falta 1%" / "Faltam 5%": Portuguese agrees with the whole part of the number, so 1,5% is still singular.
export const BUDGET_GOALS_COMPLETE = 'Tudo distribuído';
export const budgetGoalsShort = (hundredths: number): string =>
  `${hundredths >= 200 ? 'Faltam' : 'Falta'} ${formatPercent(hundredths / 100)}`;
export const budgetGoalsOver = (hundredths: number): string => `Excede em ${formatPercent(hundredths / 100)}`;

export const budgetGoalSliderLabel = (bucketName: string): string => `Meta de ${bucketName}`;
export const budgetGoalInputLabel = (bucketName: string): string => `Meta de ${bucketName}, em porcentagem`;
// While the total is off: one press settles it on the largest other group, named with its new value.
export const budgetGoalsClose = (bucketName: string, hundredths: number): string =>
  `Ajustar ${bucketName} para ${formatPercent(hundredths / 100)}`;

// What a screen reader hears about the total: once, when it changes from short to complete to over — not on every
// arrow press, when the slider already says its own value and the visible verdict would be read a second time.
export const BUDGET_GOALS_STATUS = {
  complete: 'Total de 100%: tudo distribuído',
  short: 'Total abaixo de 100%',
  over: 'Total acima de 100%',
} as const;

export const BUDGET_GOALS_DISCARD = 'Descartar';
export const BUDGET_GOALS_SAVE = 'Salvar';
export const BUDGET_GOALS_SAVING = 'Salvando...';

export const BUDGET_GOALS_LOAD_ERROR_TITLE = 'Não foi possível carregar as metas';
export const BUDGET_GOALS_LOADING = 'Carregando metas';

// The form pre-validates the same rules the server enforces (sum of exactly 100, each 0–100), so a 422 here is
// a drift signal, not something the user did — the copy restates the rules instead of blaming them (§12.2).
export const BUDGET_GOALS_INVALID = {
  title: 'Metas inválidas',
  description: 'As metas precisam somar exatamente 100% e cada uma ficar entre 0% e 100%.',
};
